import React, { useState } from "react";
import {
  useTranslate,
  IResourceComponentsProps,
  CrudFilters,
  HttpError,
  useApiUrl,
} from "@pankod/refine-core";
import {
  List,
  Table,
  TextField,
  useTable,
  getDefaultSortOrder,
  Space,
  EditButton,
  Form,
  Input,
  Icons,
  Button,
  ListButton,
  DeleteButton,
  notification,
  Modal,
  Alert,
} from "@pankod/refine-antd";
import { useNavigation } from '../../core/customNavigation';
import { IPriceZone } from "interfaces";
import { PlusSquareOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/fr'
import axios from "axios";

interface YpLog {
  level: string;
  message: string;
}

interface YpSyncResult {
  success: boolean;
  logs: YpLog[];
  sent: number;
  errors: number;
}

export const PriceZoneList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { create } = useNavigation()
  const apiUrl = useApiUrl();
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<YpSyncResult | null>(null);
  const [syncModalVisible, setSyncModalVisible] = useState(false);

  const handleSyncAll = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    setSyncModalVisible(true);

    let pricezones: IPriceZone[] = [];
    try {
      const { data } = await axios.get(`${apiUrl}/pricezones`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("ACCESS_TOKEN")}`,
        },
        params: { _end: 9999, _start: 0, _sort: 'name', _order: 'asc' },
      });
      pricezones = Array.isArray(data) ? data : data.data || [];
    } catch (e: any) {
      notification.error({ message: 'Erreur', description: `Impossible de charger les zones de prix: ${e.message}` });
      setSyncLoading(false);
      return;
    }
    const allLogs: YpLog[] = [];
    let totalSent = 0;
    let totalErrors = 0;

    allLogs.push({ level: 'info', message: `Synchronisation de ${pricezones.length} zones de prix...` });
    setSyncResult({ success: true, logs: [...allLogs], sent: 0, errors: 0 });

    for (const pz of pricezones) {
      allLogs.push({ level: 'info', message: `― ${pz.name}...` });
      setSyncResult({ success: true, logs: [...allLogs], sent: totalSent, errors: totalErrors });

      try {
        const { data } = await axios.post(
          `${apiUrl}/yieldplanet/sync`,
          { pricezone_id: pz.id },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem("ACCESS_TOKEN")}`,
              'Content-Type': 'application/json',
            },
            timeout: 300000, // 5 min, match nginx proxy_read_timeout
          }
        );
        const result = data as YpSyncResult;
        totalSent += result.sent;
        totalErrors += result.errors;
        allLogs.push(...result.logs);
      } catch (e: any) {
        totalErrors += 1;
        allLogs.push({ level: 'error', message: `${pz.name}: ${e.message}` });
      }

      setSyncResult({
        success: totalErrors === 0,
        logs: [...allLogs],
        sent: totalSent,
        errors: totalErrors,
      });
    }

    allLogs.push({
      level: totalErrors === 0 ? 'success' : 'warning',
      message: `Terminé : ${totalSent} prix envoyés${totalErrors > 0 ? `, ${totalErrors} erreurs` : ''}`,
    });

    setSyncResult({
      success: totalErrors === 0,
      logs: [...allLogs],
      sent: totalSent,
      errors: totalErrors,
    });

    if (totalErrors === 0) {
      notification.success({
        message: 'YieldPlanet',
        description: `Synchronisation terminée : ${totalSent} prix envoyés`,
        duration: 5,
      });
    } else {
      notification.warning({
        message: 'YieldPlanet',
        description: `${totalSent} prix envoyés, ${totalErrors} erreurs`,
        duration: 8,
      });
    }

    setSyncLoading(false);
  };

  const { tableProps, sorter, searchFormProps } = useTable<IPriceZone, HttpError, IPriceZoneFilterVariables>({
      onSearch: (params) => {
      const filters: CrudFilters = [];
      const { name } = params;
      filters.push(
          {
              field: "name",
              operator: "contains",
              value: name,
          },
      );
      return filters;
    },
    initialSorter: [
      {
        field: "name",
        order: "asc",
      },
    ],
  });

    const isLoading = false;

  return (
    <List 
      title={t("menu.pricezones")}
      pageHeaderProps={{
        extra: [
          <Form 
            layout="horizontal"
            onValuesChange={() => searchFormProps.form?.submit()}
            key="page_header_extra_1"
            {...searchFormProps}
          >
            <Form.Item label="" name="name" style={{ marginBottom: 0 }}>
              <Input
                  placeholder="ID, Title, Content, etc."
                  prefix={<Icons.SearchOutlined />}
              />
            </Form.Item>
          </Form>,
          <Button key="page_header_extra_2" onClick={(e) => create(t("menu.pricezones", e))}>+</Button>,
          <Button
            key="page_header_extra_3"
            type="primary"
            icon={<SyncOutlined spin={syncLoading} />}
            loading={syncLoading}
            onClick={handleSyncAll}
          >
            Sync YieldPlanet
          </Button>,
        ],
      }}
    >
      <Table
        {...tableProps} 
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        rowKey="id"
      >
        <Table.Column<IPriceZone>
          dataIndex="name"
          key="name"
          title={t("rooms.room")}
          render={(_, record) => {
            return(
              <Space>
                <TextField value={record?.name} />
                <EditButton hideText size="small" resourceNameOrRouteName="pricezones" recordItemId={record?.id}></EditButton>
                <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            )}}
  
          defaultSortOrder={getDefaultSortOrder("name", sorter)}
          sorter
        />
        <Table.Column<IPriceZone>

          title={t("table.actions")}
          dataIndex="actions"
          render={(_, record) => {
          return(
            <Space>
              {record?.yieldanalysis?.[0]?.id && <EditButton size="small" resourceNameOrRouteName="yieldanalysis" recordItemId={record?.yieldanalysis?.[0]?.id}>{`${t("buttons.edit")} ${t("menu.yield")} ${dayjs(record?.yieldanalysis?.[0]?.dateref).locale(t("short_lang")).format('ddd D/M')}`}</EditButton>}
              {<ListButton size="small" resourceNameOrRouteName="yieldanalysis"></ListButton>}
              <Button href={`${t("menu.yield")}/create/${record.id}`} size="small" icon={<PlusSquareOutlined />}>{`${t("buttons.create")} ${t("menu.yield")}`}</Button>
              </Space>
          )}}
        />
      </Table>

      <Modal
        title="Synchronisation YieldPlanet"
        visible={syncModalVisible}
        onCancel={() => setSyncModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSyncModalVisible(false)}>
            Fermer
          </Button>
        ]}
        width={600}
      >
        {syncLoading && !syncResult && (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <SyncOutlined spin style={{ fontSize: 32, color: '#1890ff' }} />
            <p style={{ marginTop: 16 }}>Synchronisation en cours...</p>
          </div>
        )}
        {syncResult && (
          <>
            <Alert
              type={syncResult.success ? 'success' : 'warning'}
              message={`${syncResult.sent} prix envoyés${syncResult.errors > 0 ? `, ${syncResult.errors} erreurs` : ''}`}
              style={{ marginBottom: 12 }}
            />
            <div style={{
              maxHeight: 300,
              overflowY: 'auto',
              background: '#1a1a2e',
              borderRadius: 6,
              padding: 12,
              fontFamily: 'monospace',
              fontSize: 12,
            }}>
              {syncResult.logs.map((log, i) => (
                <div key={i} style={{
                  color: log.level === 'error' ? '#ff4d4f' : log.level === 'warning' ? '#faad14' : log.level === 'success' ? '#52c41a' : '#b0b0b0',
                  padding: '2px 0',
                }}>
                  {log.message}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
    </List>
  );
};

interface IPriceZoneFilterVariables {
  name: string;
}
